import { createServerFn } from "@tanstack/react-start"
import { Result } from "@praha/byethrow"
import {
  getPlans as getPlansService,
  subscribe as subscribeService,
  unsubscribe as unsubscribeService,
} from "@apps/gwservice/plans"
import {
  applyForPlan as applyForPlanService,
  getPlanApplicationPrefill as getPlanApplicationPrefillService,
} from "@apps/gwservice/access-requests"
import {
  cancelPending as cancelPendingService,
  changePaymentMethod as changePaymentMethodService,
  deleteSavedCard as deleteSavedCardService,
  resolvePending as resolvePendingService,
} from "@apps/gwservice/payments"
import type { PlanApplicationSphere } from "@apps/gwservice/types"
import { withAuth } from "../../auth/server/auth"

export const getPlans = createServerFn({
  method: "GET",
})
  .handler(() =>
    Result.try({
      try: () => withAuth(getPlansService)(),
      catch: () => "unexpected server error",
    }),
  )

export const subscribe = createServerFn({
  method: "POST",
})
  .inputValidator((data: {
    planId: number
    period: "monthly" | "yearly"
    returnUrl: string
  }) => data)
  .handler(({ data }) => withAuth(subscribeService)(data.planId, data.period, data.returnUrl))

export const unsubscribe = createServerFn({
  method: "POST",
})
  .handler(() => withAuth(unsubscribeService)())

export const changePaymentMethod = createServerFn({
  method: "POST",
})
  .inputValidator((data: { returnUrl: string }) => data)
  .handler(({ data }) => withAuth(changePaymentMethodService)(data.returnUrl))

export const resolvePending = createServerFn({
  method: "POST",
})
  .handler(() => withAuth(resolvePendingService)())

export const cancelPending = createServerFn({
  method: "POST",
})
  .handler(() => withAuth(cancelPendingService)())

export const deleteSavedCard = createServerFn({
  method: "POST",
})
  .handler(() => withAuth(deleteSavedCardService)())

export type PlanApplicationFormInput = {
  lastName: string
  firstName: string
  middleName: string
  birthDate: string
  sphere: PlanApplicationSphere
  position: string
  phone: string
  email: string
  source: string
  recommender: string
  expectations: string
}

const REQUIRED_FORM_FIELDS = [
  "lastName",
  "firstName",
  "middleName",
  "birthDate",
  "sphere",
  "position",
  "phone",
  "email",
] as const

export const getPlanApplicationPrefill = createServerFn({
  method: "GET",
})
  .handler(() =>
    Result.try({
      try: () => withAuth(getPlanApplicationPrefillService)(),
      catch: () => "unexpected server error",
    }),
  )

export const applyForPlan = createServerFn({
  method: "POST",
})
  .inputValidator((data: { planId: number; form: PlanApplicationFormInput; consent: boolean }) => {
    if (!data.consent) throw new Error("Consent required")
    for (const key of REQUIRED_FORM_FIELDS) {
      const value = data.form[key]
      if (typeof value !== "string" || value.trim() === "") throw new Error(`Missing field: ${key}`)
    }
    return data
  })
  .handler(({ data }) =>
    Result.try({
      try: () => withAuth(applyForPlanService)(data.planId, data.form, data.consent),
      catch: () => "unexpected server error",
    }),
  )
